import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { analysisAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp, TrendingDown, Brain, AlertTriangle,
  PiggyBank, Target, RefreshCw, LineChart,
  ArrowUpRight, ArrowDownRight, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency } from '../lib/utils';
import {
  LineChart as RechartsLine, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const Analysis = () => {
  const { language, user } = useAuth();
  const currency = user?.currency || 'EUR';

  const [loading, setLoading] = useState(true);
  const [expenseForecast, setExpenseForecast] = useState(null);
  const [savingsProjection, setSavingsProjection] = useState(null);
  const [anomalies, setAnomalies] = useState(null);

  useEffect(() => {
    loadAnalysis();
  }, []);

  const loadAnalysis = async () => {
    setLoading(true);
    try {
      const [expenseRes, savingsRes, anomalyRes] = await Promise.all([
        analysisAPI.predictExpenses(6),
        analysisAPI.savingsProjection({ months: 12 }),
        analysisAPI.detectAnomalies()
      ]);

      setExpenseForecast(expenseRes.data);
      setSavingsProjection(savingsRes.data);
      setAnomalies(anomalyRes.data);
    } catch (error) {
      toast.error('Error al cargar análisis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="w-12 h-12 mx-auto mb-4 text-primary animate-pulse" />
          <p className="text-muted-foreground">Analizando tus finanzas...</p>
        </div>
      </div>
    );
  }

  const hasExpenseData = expenseForecast?.prediction?.predictions?.length > 0;
  const hasSavingsData = savingsProjection?.prediction?.projections?.length > 0;
  const hasAnomalies = anomalies?.anomalies && anomalies.anomalies.length > 0;
  const hasAnyData = hasExpenseData || hasSavingsData || hasAnomalies;

  return (
    <div className="space-y-6" data-testid="analysis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-foreground tracking-tight flex items-center gap-3">
            <Brain className="text-primary" />
            Análisis Predictivo
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Predicciones inteligentes basadas en tus patrones financieros
          </p>
        </div>
        <Button
          onClick={loadAnalysis}
          variant="outline"
          className="rounded-sm"
        >
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {!hasAnyData ? (
        <Card className="border-border rounded-sm shadow-none">
          <CardContent className="p-8 text-center">
            <Info className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold text-foreground mb-2">Datos insuficientes</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Necesitamos al menos una transacción (ingreso o gasto) para generar predicciones base.
              Continúa registrando tu actividad para obtener recomendaciones.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Expense Forecast */}
          {hasExpenseData && (
            <Card className="border-border rounded-sm shadow-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="text-primary" size={18} />
                    Predicción de Gastos
                  </CardTitle>
                  <div className={cn(
                    "text-xs px-2 py-1 rounded-sm",
                    expenseForecast?.confidence >= 0.7
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  )}>
                    {Math.round((expenseForecast?.confidence || 0) * 100)}% confianza
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {expenseForecast?.prediction?.predictions && (
                  <>
                    <div className="h-64 mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={expenseForecast.prediction.predictions}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12 }}
                            className="text-muted-foreground"
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            tickFormatter={(v) => `€${v}`}
                            className="text-muted-foreground"
                          />
                          <Tooltip
                            formatter={(value) => formatCurrency(value, currency)}
                            labelFormatter={(label) => `Mes: ${label}`}
                            contentStyle={{
                              borderRadius: '2px',
                              backgroundColor: 'hsl(var(--card))',
                              borderColor: 'hsl(var(--border))'
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="range_high"
                            stroke="transparent"
                            fill="#ef444430"
                            name="Máximo"
                          />
                          <Area
                            type="monotone"
                            dataKey="predicted_expense"
                            stroke="#ef4444"
                            fill="#ef444420"
                            strokeWidth={2}
                            name="Predicción"
                          />
                          <Area
                            type="monotone"
                            dataKey="range_low"
                            stroke="transparent"
                            fill="transparent"
                            name="Mínimo"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-muted/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Promedio Mensual</p>
                        <p className="font-mono text-lg font-semibold text-foreground">
                          {formatCurrency(expenseForecast.prediction.monthly_average, currency)}
                        </p>
                      </div>
                      {expenseForecast.prediction.predictions?.slice(0, 2).map((p, i) => (
                        <div key={i} className="p-3 bg-muted/50 rounded-sm">
                          <p className="text-xs text-muted-foreground">{p.month}</p>
                          <p className="font-mono text-lg font-semibold text-foreground">
                            {formatCurrency(p.predicted_expense, currency)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {expenseForecast?.recommendations?.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-sm">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-400 mb-1">
                      Recomendaciones
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                      {expenseForecast.recommendations.map((r, i) => (
                        <li key={i}>• {r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Savings Projection */}
          {hasSavingsData && (
            <Card className="border-border rounded-sm shadow-none">
              <CardHeader>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <PiggyBank className="text-primary" size={18} />
                  Proyección de Ahorro
                </CardTitle>
              </CardHeader>
              <CardContent>
                {savingsProjection?.prediction && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-muted/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Balance Actual</p>
                        <p className="font-mono text-lg font-semibold text-foreground">
                          {formatCurrency(savingsProjection.prediction.current_balance, currency)}
                        </p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-sm">
                        <p className="text-xs text-muted-foreground">Ahorro Mensual</p>
                        <p className={cn(
                          "font-mono text-lg font-semibold",
                          savingsProjection.prediction.average_monthly_savings >= 0
                            ? "text-emerald-600"
                            : "text-red-600"
                        )}>
                          {savingsProjection.prediction.average_monthly_savings >= 0 ? '+' : ''}
                          {formatCurrency(savingsProjection.prediction.average_monthly_savings, currency)}
                        </p>
                      </div>
                      <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-sm col-span-2">
                        <p className="text-xs text-emerald-700 dark:text-emerald-400">
                          Balance Proyectado (12 meses)
                        </p>
                        <p className="font-mono text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                          {formatCurrency(savingsProjection.prediction.projected_balance_end, currency)}
                        </p>
                      </div>
                    </div>

                    {savingsProjection.prediction.projections && (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLine data={savingsProjection.prediction.projections}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis
                              tick={{ fontSize: 10 }}
                              tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                            />
                            <Tooltip
                              formatter={(value) => formatCurrency(value, currency)}
                              contentStyle={{
                                borderRadius: '2px',
                                backgroundColor: 'hsl(var(--card))',
                                borderColor: 'hsl(var(--border))'
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="projected_balance"
                              stroke="#10B981"
                              strokeWidth={2}
                              dot={false}
                              name="Balance"
                            />
                          </RechartsLine>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Anomalies / General Insight (Always show if 'hasAnyData' is true but hasExpenseData is true) */}
          {(hasExpenseData || anomalies?.anomalies) && (
            <Card className="border-border rounded-sm shadow-none">
              <CardHeader>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="text-amber-500" size={18} />
                  Detección de Anomalías
                </CardTitle>
              </CardHeader>
              <CardContent>
                {anomalies?.anomalies?.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Target className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No se detectaron anomalías en tus gastos recientes</p>
                    <p className="text-xs mt-1">Tus patrones de gasto son consistentes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {anomalies?.anomalies?.map((a, i) => (
                      <div
                        key={i}
                        className={cn(
                          "p-3 rounded-sm border-l-4",
                          a.severity === 'high'
                            ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                            : "border-amber-500 bg-amber-50 dark:bg-amber-900/20"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {a.type === 'large_transaction' ? (
                              <ArrowUpRight className={cn(
                                a.severity === 'high' ? "text-red-600" : "text-amber-600"
                              )} size={16} />
                            ) : (
                              <TrendingUp className="text-amber-600" size={16} />
                            )}
                            <span className="font-medium text-sm text-foreground">
                              {a.type === 'large_transaction'
                                ? 'Transacción Inusual'
                                : 'Incremento en Categoría'
                              }
                            </span>
                          </div>
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full",
                            a.severity === 'high'
                              ? "bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200"
                              : "bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200"
                          )}>
                            {a.severity === 'high' ? 'Alta' : 'Media'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {a.description ||
                            (a.type === 'large_transaction'
                              ? `Gasto de ${formatCurrency(a.amount, currency)} el ${new Date(a.date).toLocaleDateString()}`
                              : `Incremento del ${a.increase_percent}% en esta categoría`
                            )
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Analysis;
